/**
 * PushNotificationsPrompt — Layered UX to enable Web Push (Feb 2026)
 *
 * Three layers, in this order:
 *
 *   1. SmartBanner    : a small navy-bordered banner that appears at the
 *      top of the dashboard after first login if push notifications
 *      have not been enabled. User can "Enable now" or "Later".
 *      "Later" dismisses for 7 days via localStorage.
 *
 *   2. EducationalModal : opened from the banner. Explains WHY enable
 *      notifications before triggering the browser permission popup
 *      (proven to lift opt-in rate from ~20% to ~70%).
 *
 *   3. IOSInstallGuide : shown automatically for iOS Safari users
 *      (where web push only works after the user installs the PWA to
 *      their home screen — iOS 16.4+ requirement).
 *
 * Mounted once inside `DashboardLayout` so every authenticated page
 * surfaces the prompt until the user opts in or dismisses.
 */
import React, { useEffect, useState } from 'react';
import { Bell, BellRing, X, Sparkles, ShieldCheck, Smartphone, Share, Plus, Loader2, Check, Settings, GraduationCap } from 'lucide-react';
import { ensurePushSubscription, getPushSubscriptionStatus } from '../lib/pushClient';

const DISMISS_KEY = 'mesp_push_prompt_dismissed_at';
const IOS_PWA_DISMISS_KEY = 'mesp_ios_pwa_push_gate_dismissed_at';
const DISMISS_DAYS = 7;
const GOLD = '#C9A24E';

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalonePWA = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
   window.navigator.standalone === true);

const isSupportedBrowser = () =>
  typeof window !== 'undefined' &&
  typeof Notification !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

const isDismissed = (key = DISMISS_KEY) => {
  try {
    const ts = localStorage.getItem(key);
    if (!ts) return false;
    const ageMs = Date.now() - parseInt(ts, 10);
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const dismissKey = (key) => {
  try { localStorage.setItem(key, String(Date.now())); } catch {}
};

// iPhone Home-Screen PWA uses the exact Android gate visual/copy while
// keeping a completely separate Web Push activation handler.
const IOSPwaPermissionGate = ({ onActivate, onLater, busy, permission, errorMsg }) => (
  <div
    dir="rtl"
    className="fixed inset-0 z-[9999] flex items-center justify-center p-5"
    style={{ background: 'rgba(4, 9, 26, 0.92)', backdropFilter: 'blur(8px)' }}
    data-testid="ios-pwa-notification-gate"
  >
    <div
      className="w-full max-w-md rounded-3xl overflow-hidden text-center"
      style={{
        background: 'linear-gradient(150deg, #0A1128 0%, #131F42 100%)',
        border: '1px solid rgba(201,162,78,0.4)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
      }}
    >
      <div className="pt-8 pb-2 flex justify-center">
        <div
          className="relative w-20 h-20 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(201,162,78,0.12)', border: '1.5px solid rgba(201,162,78,0.5)' }}
        >
          <BellRing size={34} style={{ color: GOLD }} className="animate-pulse" />
        </div>
      </div>

      <div className="px-6 pb-7 pt-3">
        <h2 className="text-lg font-extrabold text-white" data-testid="ios-pwa-notification-title">
          التنبيهات ركيزة أساسية في رحلتك الأكاديمية
        </h2>
        <p className="text-[13px] leading-relaxed text-white/70 mt-3" data-testid="ios-pwa-notification-description">
          حرصاً على انتظامك وتفوقك، تعتمد المنصة على التنبيهات الفورية لإبلاغك
          بمواعيد حصصك المباشرة، ونداء المعلم عند بدء الدرس، والتعليمات الأكاديمية
          العاجلة. <span className="text-white/90 font-semibold">تفعيل الإشعارات شرط
          أساسي لاستخدام خدمات المنصة وإثبات الحضور</span> — حتى لا تفوتك أي حصة.
        </p>

        <div className="flex items-center justify-center gap-2 mt-4 text-[11px] text-white/50">
          <ShieldCheck size={13} style={{ color: GOLD }} />
          نرسل لك ما يخدم مسيرتك التعليمية فقط — دون أي إزعاج غير ضروري
        </div>

        {permission === 'denied' && (
          <div
            className="mt-4 text-[11.5px] text-white/60 bg-white/5 border border-white/10 rounded-xl p-3 text-right leading-relaxed"
            data-testid="ios-pwa-notification-settings-help"
          >
            افتح <span className="text-white/90">إعدادات iPhone ← الإشعارات ← MESP</span> وفعّل
            «السماح بالإشعارات» و«شاشة القفل» و«الأصوات»، ثم عُد إلى التطبيق.
          </div>
        )}

        {errorMsg && permission !== 'denied' && (
          <div className="mt-4 p-3 rounded-xl text-[11.5px] text-red-200 bg-red-500/10 border border-red-400/30" data-testid="ios-pwa-notification-error">
            {errorMsg}
          </div>
        )}

        <button
          type="button"
          onClick={onActivate}
          disabled={busy}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-[15px] font-extrabold transition-transform active:scale-95 disabled:opacity-60"
          style={{
            background: 'linear-gradient(135deg, #E5C158, #C99A2F)',
            color: '#0A1128',
            boxShadow: '0 6px 18px rgba(201,154,47,0.45)',
          }}
          data-testid="ios-pwa-notification-activate"
        >
          {busy ? <Loader2 size={17} className="animate-spin" /> : permission === 'denied' ? <Settings size={17} /> : <BellRing size={17} />}
          {busy ? 'جاري التفعيل…' : permission === 'denied' ? 'طريقة التفعيل' : 'تفعيل الآن'}
        </button>

        <button
          type="button"
          onClick={onLater}
          disabled={busy}
          className="mt-2 w-full py-2.5 text-xs font-semibold text-white/60 transition-colors hover:text-white disabled:opacity-50"
          data-testid="ios-pwa-notification-later"
        >
          لاحقاً
        </button>

        <div className="flex items-center justify-center gap-1.5 mt-3 text-[10.5px]" style={{ color: 'rgba(201,162,78,0.8)' }}>
          <GraduationCap size={12} />
          منصة MESP التعليمية — نجاحك يبدأ من حضورك
        </div>
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────
// Layer 3 — iOS Install Guide modal
// ────────────────────────────────────────────────────────────────
const IOSInstallGuide = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    style={{ background: 'rgba(2, 6, 17, 0.78)' }}
    onClick={onClose}
    data-testid="push-ios-guide-modal"
  >
    <div
      dir="rtl"
      className="rounded-2xl max-w-md w-full overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #031B3A 0%, #08284D 100%)',
        border: '1px solid rgba(201, 162, 78, 0.45)',
        boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(201, 162, 78, 0.25)' }}>
        <div className="flex items-center gap-2">
          <Smartphone size={20} color="#C9A24E" />
          <h2 className="text-base font-bold text-white">تفعيل الإشعارات على iPhone / iPad</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="إغلاق"
          data-testid="push-ios-guide-close"
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }}
        >
          ✕
        </button>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
          لاستقبال إشعارات MESP بشكل فوري على جهازك (مثل واتساب)،
          يجب تثبيت المنصة على الشاشة الرئيسية أولاً:
        </p>

        <ol className="space-y-3">
          {[
            { icon: Share, text: 'اضغط على زر المشاركة في Safari (المربع مع السهم لأعلى).' },
            { icon: Plus,  text: 'اختر "أضف إلى الشاشة الرئيسية" (Add to Home Screen).' },
            { icon: Check, text: 'افتح MESP من الأيقونة الجديدة على شاشتك الرئيسية.' },
            { icon: Bell,  text: 'ارجع إلى الإعدادات داخل التطبيق وفعّل الإشعارات.' },
          ].map((step, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <div
                className="shrink-0 flex items-center justify-center"
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(201, 162, 78, 0.15)',
                  border: '1px solid rgba(201, 162, 78, 0.45)',
                  color: '#C9A24E', fontWeight: 700, fontSize: 13,
                }}
              >
                {idx + 1}
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  {step.text}
                </p>
              </div>
              <step.icon size={16} color="#C9A24E" style={{ marginTop: 6 }} />
            </li>
          ))}
        </ol>

        <p className="text-xs leading-relaxed mt-2 p-3 rounded-lg" style={{ background: 'rgba(201, 162, 78, 0.08)', border: '1px solid rgba(201, 162, 78, 0.22)', color: 'rgba(255,255,255,0.70)' }}>
          ⚠️ يتطلب iOS 16.4 أو أحدث. على الإصدارات الأقدم، الإشعارات
          الفورية غير مدعومة من Apple.
        </p>

        <button
          type="button"
          onClick={onClose}
          data-testid="push-ios-guide-done"
          className="w-full py-3 font-bold rounded-xl transition-colors"
          style={{ background: 'linear-gradient(90deg, #B5901C, #C9A24E, #B5901C)', color: '#0A1128' }}
        >
          حسناً، فهمت
        </button>
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────
// Layer 2 — Educational Modal (pre-permission)
// ────────────────────────────────────────────────────────────────
const EducationalModal = ({ onAccept, onCancel, busy, errorMsg }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    style={{ background: 'rgba(2, 6, 17, 0.78)' }}
    onClick={onCancel}
    data-testid="push-educational-modal"
  >
    <div
      dir="rtl"
      className="rounded-2xl max-w-md w-full overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #031B3A 0%, #08284D 100%)',
        border: '1px solid rgba(201, 162, 78, 0.45)',
        boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className="flex items-center justify-center"
            style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'rgba(201, 162, 78, 0.15)',
              border: '1px solid rgba(201, 162, 78, 0.50)',
            }}
          >
            <Bell size={22} color="#C9A24E" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">تفعيل إشعارات MESP</h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.65)' }}>
              ابقَ على اطلاع لحظي بكل ما يهمّك
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="إغلاق"
          data-testid="push-educational-close"
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }}
        >
          ✕
        </button>
      </div>

      <div className="px-5 pb-5 space-y-3">
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.82)' }}>
          ستظهر الإشعارات على شاشة جهازك مباشرة (تماماً مثل واتساب وتيليجرام)
          حتى لو لم تكن تتصفّح المنصة. أمثلة:
        </p>

        <ul className="space-y-2">
          {[
            'موافقة طلب التسجيل أو تحديث حالته.',
            'استلام دفعة مالية أو تأكيد التحويل.',
            'انضمام طالب جديد لقاعتك (للمعلمين).',
            'تذكرة دعم جديدة أو رد على تذكرتك.',
            'تذكير بانتهاء الاشتراك قبل أيام.',
          ].map((item, idx) => (
            <li key={idx} className="flex items-start gap-2.5">
              <div
                className="shrink-0 flex items-center justify-center mt-0.5"
                style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.18)',
                  border: '1px solid rgba(16, 185, 129, 0.50)',
                }}
              >
                <Check size={11} color="#34D399" strokeWidth={3} />
              </div>
              <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.82)' }}>{item}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(201, 162, 78, 0.06)', border: '1px solid rgba(201, 162, 78, 0.20)' }}>
          <ShieldCheck size={16} color="#C9A24E" className="shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.70)' }}>
            نحترم خصوصيتك. لن نرسل إعلانات أو إشعارات تسويقية —
            فقط أحداث تخصّك أنت مباشرة. يمكنك إيقافها متى شئت من
            الإعدادات.
          </p>
        </div>

        {errorMsg && (
          <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239, 68, 68, 0.10)', border: '1px solid rgba(239, 68, 68, 0.40)', color: '#FCA5A5' }} data-testid="push-educational-error">
            {errorMsg}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="push-educational-decline"
            className="flex-1 py-3 font-semibold rounded-xl transition-colors"
            style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
          >
            ليس الآن
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={busy}
            data-testid="push-educational-accept"
            className="flex-[1.4] py-3 font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
            style={{ background: 'linear-gradient(90deg, #B5901C, #C9A24E, #B5901C)', color: '#0A1128', boxShadow: '0 4px 14px rgba(201, 162, 78, 0.30)' }}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
            {busy ? 'جاري التفعيل…' : 'تفعيل الإشعارات'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────
// Layer 1 — Smart Banner (entry point)
// ────────────────────────────────────────────────────────────────
export const PushNotificationsPrompt = () => {
  const [permission, setPermission] = useState(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [showBanner, setShowBanner]   = useState(false);
  const [showModal,  setShowModal]    = useState(false);
  const [showIOS,    setShowIOS]      = useState(false);
  const [showIOSPwa, setShowIOSPwa]   = useState(false);
  const [subscriptionVerified, setSubscriptionVerified] = useState(false);
  const [busy,       setBusy]         = useState(false);
  const [errorMsg,   setErrorMsg]     = useState('');
  const [success,    setSuccess]      = useState(false);

  // Decide visibility on mount.
  useEffect(() => {
    const supported = isSupportedBrowser();
    const installedIOSPwa = isIOS() && isStandalonePWA();
    if (installedIOSPwa && supported) {
      let cancelled = false;
      let timer = null;
      getPushSubscriptionStatus().then(async (status) => {
        if (cancelled) return;
        setPermission(status.permission);
        if (status.permission === 'granted' && status.subscribed) {
          // Re-persist the existing endpoint idempotently in case the user
          // restored/reinstalled the PWA or the server record was lost.
          const repaired = await ensurePushSubscription();
          if (cancelled) return;
          if (repaired.ok) {
            setSubscriptionVerified(true);
            setShowIOSPwa(false);
            return;
          }
        }
        if (isDismissed(IOS_PWA_DISMISS_KEY)) return;
        timer = setTimeout(() => { if (!cancelled) setShowIOSPwa(true); }, 700);
      }).catch(() => {
        if (!cancelled && !isDismissed(IOS_PWA_DISMISS_KEY)) {
          timer = setTimeout(() => setShowIOSPwa(true), 700);
        }
      });
      return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
      };
    }
    if (!supported) {
      // iOS Safari (not standalone) → push works only via installed PWA.
      if (isIOS() && !isStandalonePWA() && !isDismissed()) {
        // Delay 4s so the prompt doesn't blast on login.
        const t = setTimeout(() => setShowBanner(true), 4000);
        return () => clearTimeout(t);
      }
      return;
    }
    if (permission === 'granted') return;       // already enabled — nothing to do
    if (isDismissed()) return;                  // user said "later" recently
    const t = setTimeout(() => setShowBanner(true), 4000);
    return () => clearTimeout(t);
  }, [permission]);

  const dismissForAWhile = () => {
    dismissKey(DISMISS_KEY);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleBannerEnable = () => {
    if (isIOS() && !isStandalonePWA()) {
      setShowIOS(true);
    } else {
      setShowModal(true);
    }
  };

  const handleAccept = async () => {
    setBusy(true); setErrorMsg('');
    try {
      const r = await ensurePushSubscription();
      if (r.ok) {
        setPermission('granted');
        setSubscriptionVerified(true);
        setSuccess(true);
        setShowModal(false);
        setShowIOSPwa(false);
        setShowBanner(false);
        // Show fleeting "enabled" confirmation banner.
        setTimeout(() => setSuccess(false), 4000);
      } else if (r.reason === 'permission_denied') {
        setPermission('denied');
        setErrorMsg('رفض المتصفح إذن الإشعارات. يمكنك تفعيلها لاحقاً من إعدادات المتصفح.');
      } else if (r.reason === 'no_key') {
        setErrorMsg('مفتاح الإشعارات غير مهيأ على الخادم.');
      } else if (r.reason === 'unsupported') {
        setErrorMsg('متصفحك لا يدعم الإشعارات الفورية.');
        setShowModal(false);
      } else {
        setErrorMsg(r.error || 'تعذّر التفعيل، حاول مجدداً.');
      }
    } catch (e) {
      setErrorMsg('تعذّر التفعيل، حاول مجدداً.');
    } finally {
      setBusy(false);
    }
  };

  const handleIOSPwaActivate = () => {
    if (permission === 'denied') {
      setErrorMsg('فعّل الإشعارات من إعدادات iPhone ثم عُد إلى التطبيق.');
      return;
    }
    handleAccept();
  };

  const dismissIOSPwaForAWhile = () => {
    dismissKey(IOS_PWA_DISMISS_KEY);
    setShowIOSPwa(false);
    setErrorMsg('');
  };

  // Don't render anything if user already opted-in.
  if (permission === 'granted' && !success && (!isIOS() || !isStandalonePWA() || subscriptionVerified)) return null;

  return (
    <>
      {/* Success toast — appears for 4s after enabling */}
      {success && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[110] px-5 py-3 rounded-full flex items-center gap-2 shadow-lg"
          style={{ background: 'linear-gradient(90deg, #047857, #10B981)', color: '#fff' }}
          data-testid="push-enabled-toast"
        >
          <Check size={18} />
          <span className="text-sm font-semibold">تم تفعيل الإشعارات بنجاح</span>
        </div>
      )}

      {/* Layer 1 — Banner */}
      {showBanner && !showModal && !showIOS && (
        <div
          dir="rtl"
          className="sticky top-0 z-[30] px-3 sm:px-4 py-2.5"
          style={{
            background: 'linear-gradient(90deg, rgba(201, 162, 78, 0.10) 0%, rgba(3, 27, 58, 0.95) 50%, rgba(201, 162, 78, 0.10) 100%)',
            borderBottom: '1px solid rgba(201, 162, 78, 0.40)',
            backdropFilter: 'blur(8px)',
          }}
          data-testid="push-prompt-banner"
        >
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <div
              className="shrink-0 flex items-center justify-center"
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(201, 162, 78, 0.20)',
                border: '1px solid rgba(201, 162, 78, 0.55)',
              }}
            >
              <Sparkles size={18} color="#C9A24E" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white leading-tight">
                لا تفوّت أي تحديث مهم
              </p>
              <p className="text-xs leading-snug" style={{ color: 'rgba(255,255,255,0.70)' }}>
                فعّل الإشعارات لتصلك مباشرة على جهازك مثل واتساب وتيليجرام.
              </p>
            </div>
            <button
              type="button"
              onClick={handleBannerEnable}
              data-testid="push-prompt-enable"
              className="shrink-0 px-3 py-2 text-xs sm:text-sm font-bold rounded-full transition-all"
              style={{ background: 'linear-gradient(90deg, #B5901C, #C9A24E, #B5901C)', color: '#0A1128' }}
            >
              تفعيل الآن
            </button>
            <button
              type="button"
              onClick={dismissForAWhile}
              aria-label="لاحقاً"
              data-testid="push-prompt-dismiss"
              className="shrink-0 ms-2 w-8 h-8 flex items-center justify-center rounded-full transition-colors hover:bg-white/15"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Layer 2 — Educational modal */}
      {showModal && (
        <EducationalModal
          onAccept={handleAccept}
          // PROD AUDIT FIX (Feb 2026) — cancelling the modal must also
          // dismiss the banner for N days, otherwise the prompt re-shows
          // on every page navigation and harasses the user.
          onCancel={() => { setShowModal(false); setErrorMsg(''); dismissForAWhile(); }}
          busy={busy}
          errorMsg={errorMsg}
        />
      )}

      {/* Layer 3 — iOS install guide */}
      {showIOS && (
        <IOSInstallGuide onClose={() => { setShowIOS(false); dismissForAWhile(); }} />
      )}

      {showIOSPwa && (
        <IOSPwaPermissionGate
          onActivate={handleIOSPwaActivate}
          onLater={dismissIOSPwaForAWhile}
          busy={busy}
          permission={permission}
          errorMsg={errorMsg}
        />
      )}
    </>
  );
};

export default PushNotificationsPrompt;
